/**
 * The scrolling message thread: persisted messages, the live streaming
 * bubble, and a minimal pending pulse for the model's thinking pause before
 * the first token (the stream can be silent for a few seconds — without
 * this it reads as a hang). Auto-scrolls to the newest content, adapted
 * from aurora's chat Content.tsx scroll pattern.
 *
 * Each consecutive same-sender run opens with an identity header (avatar +
 * name + time), and day boundaries get a date divider — both adapted from
 * aurora's chat Content/MessageHeader.
 */

import { Fragment, useEffect, useMemo, useRef } from 'react';
import { Divider, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'providers/AuthProvider';
import { ToolActivity } from 'services/api/assistant';
import AssistantMessageBubble from './AssistantMessageBubble';
import AssistantTruncatedHint from './AssistantTruncatedHint';
import AssistantTypingIndicator from './AssistantTypingIndicator';
import AssistantWriteReceipt from './AssistantWriteReceipt';
import { ChatMessage } from './useAssistantChat';

interface AssistantConversationProps {
  messages: ChatMessage[];
  streamingText: string;
  isStreaming: boolean;
  toolActivity?: ToolActivity | null;
}

const AssistantConversation = ({
  messages,
  streamingText,
  isStreaming,
  toolActivity,
}: AssistantConversationProps) => {
  const { t, i18n } = useTranslation();
  const { sessionUser } = useAuth();
  const endRef = useRef<HTMLDivElement | null>(null);
  const rootRef = useRef<HTMLDivElement | null>(null);
  // Whether the reader is pinned to the bottom — a ref (not state) so scroll
  // tracking never re-renders the thread on every scroll event or token.
  const pinnedRef = useRef(true);

  const userName = sessionUser?.name;
  const userAvatar = sessionUser?.avatar;

  // i18n.language can be hyphen-less ("elGR") — normalize to a valid tag.
  const dateLocale = (i18n.language || 'en').startsWith('el') ? 'el' : 'en';

  // Group by day (divider) + by consecutive sender (identity header). A new
  // day always re-opens a header, so a bubble under a divider is never
  // orphaned from its avatar. Intl (not dayjs) for the date — its `el` data
  // gives the genitive month a Greek actually says («13 Ιουλίου», not
  // «13 Ιούλιος»).
  const rows = useMemo(() => {
    const dateFormatter = new Intl.DateTimeFormat(dateLocale, {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
    let prevDate = '';
    let prevRole: string | undefined;
    return messages.map((m) => {
      const dateLabel = dateFormatter.format(new Date(m.createdAt));
      const showDate = dateLabel !== prevDate;
      const showHeader = showDate || m.role !== prevRole;
      prevDate = dateLabel;
      prevRole = m.role;
      return { message: m, dateLabel, showDate, showHeader };
    });
  }, [messages, dateLocale]);

  // The live streaming reply opens a fresh assistant run whenever the last
  // persisted turn wasn't already the assistant's.
  const streamingShowHeader = messages[messages.length - 1]?.role !== 'assistant';

  // Track pinned-to-bottom on the drawer's scroll container (this component's
  // parent), so a live stream can follow the newest tokens without hijacking a
  // reader who scrolled up to re-read an earlier part of a long reply (UX-7).
  useEffect(() => {
    const scroller = rootRef.current?.parentElement;
    if (!scroller) return;
    const onScroll = () => {
      const gap = scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight;
      pinnedRef.current = gap < 80;
    };
    scroller.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => scroller.removeEventListener('scroll', onScroll);
  }, []);

  // A newly appended message (a send, or a completed turn) always reveals itself.
  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages.length]);

  // Live token growth only follows when the reader is still at the bottom.
  useEffect(() => {
    if (pinnedRef.current) endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [streamingText, isStreaming]);

  // Per-write-tool labels; unmapped write tools fall back to a generic
  // "saving" line, reads fall back to "checking".
  const WRITE_LABEL_BY_TOOL: Record<string, string> = {
    create_task: 'assistant.status.creating_task',
    add_guest: 'assistant.status.adding_guest',
    update_guest: 'assistant.status.updating_guest',
    update_task: 'assistant.status.updating_task',
    create_vendor: 'assistant.status.adding_vendor',
    update_vendor: 'assistant.status.updating_vendor',
    create_payment: 'assistant.status.adding_payment',
    update_payment: 'assistant.status.updating_payment',
    create_decision: 'assistant.status.adding_decision',
    update_decision: 'assistant.status.updating_decision',
    create_table: 'assistant.status.adding_table',
    update_table: 'assistant.status.updating_table',
    approve_guest: 'assistant.status.approving_guest',
    set_rsvp: 'assistant.status.recording_rsvp',
    seat_guests: 'assistant.status.seating_guests',
    move_guests: 'assistant.status.moving_guests',
    unseat_guests: 'assistant.status.unseating_guests',
    update_privacy: 'assistant.status.updating_privacy',
    update_gifts: 'assistant.status.updating_gifts',
    set_rsvp_deadline: 'assistant.status.setting_deadline',
    update_venue: 'assistant.status.updating_venue',
    update_story: 'assistant.status.updating_story',
    add_schedule_entries: 'assistant.status.adding_schedule',
    update_schedule_entry: 'assistant.status.updating_schedule',
    update_families: 'assistant.status.updating_families',
    update_honor_attendants: 'assistant.status.updating_honors',
    set_seating_preferences: 'assistant.status.saving_seating_prefs',
    plan_seating: 'assistant.status.planning_seating',
    apply_seating_plan: 'assistant.status.applying_seating',
    revert_seating_plan: 'assistant.status.reverting_seating',
  };
  const toolLabel = toolActivity
    ? t(
        toolActivity.kind === 'write'
          ? (WRITE_LABEL_BY_TOOL[toolActivity.name] ?? 'assistant.status.saving')
          : 'assistant.status.checking',
      )
    : undefined;

  return (
    <Stack ref={rootRef} direction="column" spacing={1.25} sx={{ p: { xs: 2, md: 2.5 } }}>
      {rows.map(({ message: m, dateLabel, showDate, showHeader }, i) => (
        <Fragment key={m.id}>
          {showDate && (
            <Divider flexItem sx={{ my: 0.5 }}>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {dateLabel}
              </Typography>
            </Divider>
          )}
          <AssistantMessageBubble
            role={m.role}
            content={m.content}
            prevRole={i > 0 ? rows[i - 1].message.role : undefined}
            showHeader={showHeader}
            createdAt={m.createdAt}
            userName={userName}
            userAvatar={userAvatar}
            footer={
              m.receipts?.length || m.truncated ? (
                <Stack direction="column" spacing={0.75}>
                  {/* What actually happened first, then any hint about the text. */}
                  {m.receipts?.length ? <AssistantWriteReceipt receipts={m.receipts} /> : null}
                  {m.truncated ? <AssistantTruncatedHint /> : null}
                </Stack>
              ) : undefined
            }
          />
        </Fragment>
      ))}

      {/* Live region — the streaming reply and the typing/tool status announce
          to screen readers as they change, so AT users aren't left in silence
          after "Thinking…" (UX-8). Nested Stack preserves the row spacing. */}
      <Stack direction="column" spacing={1.25} aria-live="polite" aria-atomic="false">
        {isStreaming && streamingText && (
          <AssistantMessageBubble
            role="assistant"
            content={streamingText}
            showHeader={streamingShowHeader}
          />
        )}

        {/* Dots for the pre-first-token pause; labeled dots while a tool runs
            (a tool round can also happen mid-reply, after text has streamed). */}
        {isStreaming && (!streamingText || toolActivity) && (
          <AssistantTypingIndicator label={toolLabel} />
        )}
      </Stack>

      <div ref={endRef} />
    </Stack>
  );
};

export default AssistantConversation;
